import Utility from "../Base/Utility";
import XMLReader from "../Base/XMLReader";
import GomlParser from "../Node/GomlParser";
/**
 * interface for operate multicast nodes.
 */
export default class NodeInterface {
    constructor(nodes) {
        if (!nodes) {
            throw new Error("nodes is null");
        }
        this.nodes = nodes;
    }
    /**
     * get count of targeted nodes.
     * @return {number} [description]
     */
    get count() {
        if (this.nodes.length === 0) {
            return 0;
        }
        const counts = this.nodes.map(nodes => nodes.length);
        return Utility.sum(counts);
    }
    get isEmpty() {
        return this.count === 0;
    }
    get(i1, i2) {
        if (i1 === void 0) {
            const first = this.first();
            if (!first) {
                throw new Error("this NodeInterface is empty.");
            }
            else {
                return first;
            }
        }
        else if (i2 === void 0) {
            if (this.count <= i1) {
                throw new Error("index out of range.");
            }
            let c = i1;
            return this.find(() => {
                if (c === 0) {
                    return true;
                }
                c--;
                return false;
            });
        }
        else {
            if (this.nodes.length <= i1 || this.nodes[i1].length <= i2) {
                throw new Error("index out of range.");
            }
            else {
                return this.nodes[i1][i2];
            }
        }
    }
    getAttribute(attrName) {
        const first = this.first();
        if (!first) {
            throw new Error("this NodeInterface is empty.");
        }
        return first.getAttribute(attrName);
    }
    setAttribute(attrName, value) {
        this.forEach(node => {
            node.setAttribute(attrName, value, false);
        });
    }
    /**
     * 対象ノードにイベントリスナを追加します。
     * @param {string}   eventName [description]
     * @param {ListenerFunction} listener  [description]
     */
    on(eventName, listener) {
        this.forEach(node => {
            node.on(eventName, listener);
        });
        return this;
    }
    /**
     * 対象ノードに指定したイベントリスナが登録されていれば削除します
     * @param {string}   eventName [description]
     * @param {ListenerFunction} listener  [description]
     */
    off(eventName, listener) {
        this.forEach(node => {
            node.removeListener(eventName, listener);
        });
        return this;
    }
    /**
     * このノードインタフェースが対象とするノードそれぞれに、
     * タグで指定したノードを子要素として追加します。
     * @param {string} tag [description]
     */
    append(tag) {
        this.forEach(node => {
            const elems = XMLReader.parseXML(tag);
            elems.forEach(elem => {
                let child = GomlParser.parse(elem);
                node.addChild(child);
            });
        });
        return this;
    }
    /**
     * このノードインタフェースが対象とするノードをツリーから削除します。s
     * @param {GomlNode} child [description]
     */
    remove() {
        this.forEach(node => {
            node.remove();
        });
        return this;
    }
    /**
     * execute provided function once for each element targeted by this interface.
     * @param  {number} callback Function to execute for each element, taking three arguments:
     * abort the iteration if function returns True.
     * @return {[type]}          [description]
     */
    forEach(callback) {
        for (let i = 0; i < this.nodes.length; i++) {
            const array = this.nodes[i];
            const gomlIndex = i;
            for (let j = 0; j < array.length; j++) {
                const node = array[j];
                const nodeIndex = j;
                const ih = { abort: false };
                callback(node, gomlIndex, nodeIndex, ih);
                if (ih.abort) {
                    return this;
                }
            }
        }
        return this;
    }
    map(func) {
        return this.nodes.map((array, gomlIndex) => {
            return array.map((node, nodeIndex) => {
                return func(node, gomlIndex, nodeIndex);
            });
        });
    }
    find(predicate) {
        const nodes = this.nodes;
        for (let i = 0; i < nodes.length; i++) {
            const array = nodes[i];
            for (let j = 0; j < array.length; j++) {
                const node = array[j];
                if (predicate(node, i, j)) {
                    return node;
                }
            }
        }
        return null;
    }
    watch(attrName, watcher, immediate = false) {
        this.forEach(node => {
            node.watch(attrName, watcher, immediate);
        });
    }
    /**
     * このノードインタフェースが対象とするノードを有効、または無効にします。
     * @param {boolean} enable [description]
     */
    setEnable(enable) {
        this.forEach(node => {
            node.enabled = !!enable;
        });
        return this;
    }
    /**
     * このノードインタフェースが対象とするノードのそれぞれの子ノードを対象とする、
     * 新しいノードインタフェースを取得します。
     * @return {NodeInterface} [description]
     */
    children() {
        const children = this.nodes.map(nodes => {
            return Utility.flatMap(nodes, node => {
                return node.children;
            });
        });
        return new NodeInterface(children);
    }
    /**
     * 対象ノードにコンポーネントをアタッチします。
     * @param {Component} component [description]
     */
    addComponent(componentId, attributes = {}) {
        this.forEach(node => {
            node.addComponent(componentId, attributes);
        });
        return this;
    }
    /**
     * 最初の対象ノードを取得する
     * ひとつもなければnull
     * @return {GomlNode} [description]
     */
    first() {
        return this.find(() => true);
    }
    /**
     * 対象となる唯一のノードを取得する。
     * 対象が存在しない、あるいは複数存在するときは例外を投げる。
     * @return {GomlNode} [description]
     */
    single() {
        if (this.count !== 1) {
            throw new Error("this nodeInterface is not single.");
        }
        const first = this.first();
        if (!first) {
            throw new Error("this nodeInterface is not single,but is empty.");
        }
        return first;
    }
    filter(predicate) {
        const newNodes = [];
        for (let i = 0; i < this.nodes.length; i++) {
            const goml = this.nodes[i];
            newNodes.push([]);
            for (let j = 0; j < goml.length; j++) {
                const node = goml[j];
                if (predicate(node, i, j)) {
                    newNodes[i].push(node);
                }
            }
        }
        return new NodeInterface(newNodes);
    }
    toArray() {
        return Utility.flat(this.nodes);
    }
    addChildByName(nodeName, attributes) {
        return new NodeInterface(this.map(node => {
            return node.addChildByName(nodeName, attributes);
        }));
    }
    sendMessage(message, args) {
        this.forEach(node => {
            node.sendMessage(message, args);
        });
    }
    broadcastMessage(arg1, arg2, arg3) {
        if (typeof arg1 === "number") {
            this.forEach(node => {
                node.broadcastMessage(arg1, arg2, arg3);
            });
        }
        else {
            this.forEach(node => {
                node.broadcastMessage(arg2, arg3);
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/Interface/NodeInterface.ts